import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, SearchField, Avatar, Chip, Spinner, Kbd } from 'src/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faClock,
  faUser,
  faFileText,
  faBookOpen,
  faFile
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { searchAll, SearchResult } from 'src/api/search';
import { debounce } from 'lodash';
import DocumentsApi from 'src/api/documents';

interface GlobalSearchProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onOpenChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const isMacLike =
    typeof navigator !== 'undefined' && /(Mac|iPhone|iPad|iPod)/i.test(navigator.userAgent);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchQuery: string) => {
        try {
          const response = await searchAll({
            query: searchQuery,
            limit: 8
          });
          setResults(response.results);
        } catch (error) {
          console.error('Search error:', error);
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      }, 300),
    []
  );

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setIsLoading(false);
      debouncedSearch.cancel();
      return;
    }

    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let rafId: number | null = null;
    let attempts = 0;

    const focusInput = () => {
      attempts += 1;
      const input = inputRef.current;

      if (!input) {
        if (attempts < 8) {
          retryTimeout = setTimeout(focusInput, 40);
        }
        return;
      }

      input.focus({ preventScroll: true });
      input.setSelectionRange(input.value.length, input.value.length);

      if (document.activeElement !== input && attempts < 8) {
        retryTimeout = setTimeout(focusInput, 40);
      }
    };

    rafId = requestAnimationFrame(focusInput);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [isOpen, debouncedSearch]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!query.trim()) {
      debouncedSearch.cancel();
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debouncedSearch(query);

    return () => {
      debouncedSearch.cancel();
    };
  }, [query, isOpen, debouncedSearch]);

  const handleResultClick = (result: SearchResult) => {
    onOpenChange(false);

    if (result.type === 'campaigns' && result.dbInfo?.campaign) {
      navigate(`/campaigns/${result.dbInfo.campaign.id}`);
      return;
    }

    if (result.type === 'sessions' && result.metadata.sessionId) {
      navigate(`/sessions/${result.metadata.sessionId}/transcript`);
      return;
    }

    if (
      (result.type === 'transcripts' || result.type === 'summaries' || result.type === 'recaps') &&
      result.metadata.sessionId
    ) {
      let tabParam = '?tab=transcript';
      if (result.type === 'summaries') tabParam = '?tab=summary';
      if (result.type === 'recaps') tabParam = '?tab=notes';
      navigate(`/sessions/${result.metadata.sessionId}/transcript${tabParam}`);
      return;
    }

    if (
      result.type === 'compendium' &&
      result.metadata.campaignId &&
      result.metadata.compendiumSlug
    ) {
      navigate(`/compendium/${result.metadata.campaignId}/${result.metadata.compendiumSlug}`);
      return;
    }

    if (result.type === 'documents' && result.metadata.documentId) {
      void handleDocumentClick(result.metadata.documentId);
    }
  };

  const handleDocumentClick = async (documentId: string) => {
    try {
      const response = await DocumentsApi.getDocumentViewUrl(documentId);
      const { viewUrl } = response;
      if (!viewUrl) {
        console.error('No viewUrl in response:', response);
        return;
      }

      window.open(viewUrl, '_blank');
    } catch (error) {
      console.error('Failed to get document view URL:', error);
    }
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'campaigns':
        return faBookOpen;
      case 'sessions':
        return faClock;
      case 'transcripts':
        return faUser;
      case 'summaries':
      case 'recaps':
        return faFileText;
      case 'documents':
        return faFile;
      case 'compendium':
        return faBookOpen;
      default:
        return faFileText;
    }
  };

  const getResultTypeColor = (type: string) => {
    switch (type) {
      case 'campaigns':
        return 'accent';
      case 'sessions':
        return 'default';
      case 'transcripts':
        return 'success';
      case 'summaries':
        return 'warning';
      case 'recaps':
        return 'danger';
      case 'compendium':
        return 'accent';
      case 'documents':
      default:
        return 'default';
    }
  };

  const getDisplayType = (type: string) => {
    switch (type) {
      case 'campaigns':
        return 'campaign';
      case 'sessions':
        return 'session';
      case 'transcripts':
        return 'transcript';
      case 'summaries':
        return 'summary';
      case 'recaps':
        return 'highlight';
      case 'players':
        return 'player';
      case 'documents':
        return 'document';
      case 'compendium':
        return 'compendium';
      default:
        return type;
    }
  };

  const formatResultContent = (result: SearchResult) => {
    const maxLength = 180;
    return result.content.length > maxLength
      ? result.content.substring(0, maxLength) + '...'
      : result.content;
  };

  const formatResultTitle = (result: SearchResult) => {
    if (result.dbInfo?.campaign?.title && result.dbInfo?.session?.title) {
      return `${result.dbInfo.campaign.title} • ${result.dbInfo.session.title}`;
    }
    if (result.dbInfo?.campaign?.title) {
      return result.dbInfo.campaign.title;
    }
    if (result.metadata.campaignTitle && result.metadata.sessionTitle) {
      return `${result.metadata.campaignTitle} • ${result.metadata.sessionTitle}`;
    }
    return result.metadata.title ?? result.metadata.campaignTitle ?? 'Search Result';
  };

  const formatResultSubtitle = (result: SearchResult) => {
    if (result.type === 'transcripts' && result.metadata.speaker) {
      return result.metadata.speaker;
    }
    if (result.type === 'summaries') {
      return 'Session Summary';
    }
    if (result.type === 'recaps') {
      return 'Session Highlight';
    }
    if (result.type === 'compendium') {
      return result.metadata.entityTypeLabel
        ? `Compendium · ${result.metadata.entityTypeLabel}`
        : 'Compendium';
    }
    return result.type.charAt(0).toUpperCase() + result.type.slice(1);
  };

  const sortedResults = useMemo(() => {
    return [...results].sort((a, b) => b.score - a.score);
  }, [results]);

  return (
    <Modal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange} variant="blur">
      <Modal.Container placement="center" scroll="outside">
        <Modal.Dialog className="w-full max-w-3xl">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>Search</Modal.Heading>
          </Modal.Header>
          <Modal.Body className="pt-0">
            <SearchField
              aria-label="Global search"
              name="global-search"
              value={query}
              onChange={setQuery}
              variant="secondary">
              <SearchField.Group>
                <SearchField.SearchIcon>
                  <FontAwesomeIcon icon={faSearch} />
                </SearchField.SearchIcon>
                <SearchField.Input
                  ref={inputRef}
                  autoFocus
                  className="w-full"
                  placeholder="Search campaigns, sessions, transcripts..."
                />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>

            <div className="mt-4 max-h-[60vh] overflow-y-auto rounded-xl border border-divider">
              {!query.trim() ? (
                <div className="p-4 text-center text-sm opacity-70">Start typing to search...</div>
              ) : isLoading ? (
                <div className="flex items-center justify-center gap-2 p-6 text-sm opacity-80">
                  <Spinner size="sm" />
                  Searching...
                </div>
              ) : sortedResults.length === 0 ? (
                <div className="p-4 text-center text-sm opacity-70">No results found</div>
              ) : (
                sortedResults.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    type="button"
                    className="flex w-full cursor-pointer items-start gap-4 border-b border-divider p-4 text-left transition-opacity last:border-b-0 hover:opacity-90">
                    <Avatar className="shrink-0">
                      <Avatar.Fallback>
                        <FontAwesomeIcon icon={getResultIcon(result.type)} />
                      </Avatar.Fallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            {formatResultTitle(result)}
                          </div>
                          <div className="mt-1 text-xs opacity-80">
                            {formatResultSubtitle(result)}
                          </div>
                          <div className="mt-1 line-clamp-2 text-xs leading-relaxed opacity-70">
                            {formatResultContent(result)}
                          </div>
                        </div>
                        <div className="shrink-0">
                          <Chip
                            size="sm"
                            variant="soft"
                            color={
                              getResultTypeColor(result.type) as
                                | 'accent'
                                | 'default'
                                | 'success'
                                | 'warning'
                                | 'danger'
                            }>
                            {getDisplayType(result.type)}
                          </Chip>
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="flex items-center justify-between pb-1 pt-2 text-xs opacity-70">
              <span className="inline-flex items-center gap-2">
                Tip: Use
                <Kbd>
                  <Kbd.Abbr keyValue={isMacLike ? 'command' : 'ctrl'} />
                  <Kbd.Content>K</Kbd.Content>
                </Kbd>
                to open search quickly
              </span>
            </div>
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
};

export default GlobalSearch;
